import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { Building2, CalendarDays, FileText, Handshake, Image, LayoutDashboard, Mail, Megaphone, Menu, Settings, Shield, Trophy, UserCog, Users, X } from "lucide-react";
import { Toaster } from "sonner";
import Seo from "@/components/site/Seo";
import { Logo } from "@/components/site/Logo";
import { useAdminApi } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { group: "Enquiries", admin: true },
  { to: "/admin/enquiries/player", label: "Player registrations", icon: Users, admin: true },
  { to: "/admin/enquiries/franchise", label: "Franchise enquiries", icon: Building2, admin: true },
  { to: "/admin/enquiries/sponsor", label: "Sponsor enquiries", icon: Handshake, admin: true },
  { to: "/admin/enquiries/media", label: "Media enquiries", icon: Megaphone, admin: true },
  { to: "/admin/enquiries/contact", label: "Contact messages", icon: Mail, admin: true },
  { group: "League" },
  { to: "/admin/content/teams", label: "Teams", icon: Shield },
  { to: "/admin/content/players", label: "Player profiles", icon: Users },
  { to: "/admin/content/matches", label: "Matches & scorecards", icon: CalendarDays },
  { to: "/admin/content/standings", label: "Points table", icon: Trophy },
  { to: "/admin/content/venues", label: "Venues", icon: Building2 },
  { group: "Content" },
  { to: "/admin/content/news", label: "News", icon: FileText },
  { to: "/admin/content/gallery", label: "Gallery", icon: Image },
  { to: "/admin/content/sponsors", label: "Approved sponsors", icon: Handshake },
  { to: "/admin/settings", label: "Homepage & announcements", icon: Settings },
  { to: "/admin/users", label: "Admin users", icon: UserCog, admin: true },
];

const Sidebar = ({ role, onNav }) => (
  <nav className="space-y-0.5 p-3" aria-label="Admin">
    {NAV.filter((n) => !n.admin || role === "admin").map((n, i) => n.group
      ? <p key={i} className="px-3 pb-1 pt-5 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">{n.group}</p>
      : (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNav} data-testid={`admin-nav-${n.to.split("/").pop()}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors ${isActive ? "bg-[#00E5FF]/10 font-semibold text-[#00E5FF]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
          <n.icon className="h-4 w-4" />{n.label}
        </NavLink>
      ))}
  </nav>
);

const Pending = () => (
  <div data-testid="admin-access-pending" className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
    <Shield className="h-10 w-10 text-[#FF5722]" />
    <h1 className="font-display text-4xl uppercase">Access pending</h1>
    <p className="max-w-md text-slate-400">Your account is signed in but has not been granted admin access. Ask a league administrator to assign you a role.</p>
    <div className="flex items-center gap-4"><UserButton /><Link to="/" className="text-sm text-[#00E5FF]">Back to website</Link></div>
  </div>
);

function Shell() {
  const req = useAdminApi();
  const { isSignedIn } = useAuth();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isSignedIn) { req("get", "/me").then(setMe).catch(() => setMe({ role: "member" })); }
  }, [isSignedIn, req]);

  if (!me) return <div className="flex min-h-screen items-center justify-center text-slate-400" data-testid="admin-loading">Loading dashboard…</div>;
  if (!["admin", "editor"].includes(me.role)) return <Pending />;
  return (
    <div data-testid="admin-shell" className="min-h-screen bg-[#0A192F] lg:grid lg:grid-cols-[260px_1fr]">
      <aside className={`fixed inset-y-0 left-0 z-40 w-[260px] overflow-y-auto border-r border-white/10 bg-[#071325] transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center border-b border-white/10 px-4"><Logo compact={false} testid="admin-logo" /></div>
        <Sidebar role={me.role} onNav={() => setOpen(false)} />
      </aside>
      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-white/10 bg-[#0A192F]/90 px-4 backdrop-blur-xl sm:px-6">
          <button type="button" className="rounded-md border border-white/15 p-2 lg:hidden" onClick={() => setOpen((v) => !v)} data-testid="admin-menu-toggle" aria-label="Toggle admin menu">
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          <p className="hidden text-xs font-bold uppercase tracking-[0.25em] text-slate-400 lg:block">League Control Room</p>
          <div className="flex items-center gap-4">
            <span data-testid="admin-role" className="rounded-full border border-[#00E5FF]/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#00E5FF]">{me.role}</span>
            <Link to="/" className="text-xs text-slate-400 hover:text-white">View site</Link>
            <UserButton />
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet context={{ me }} /></main>
      </div>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

export default function AdminLayout() {
  return (
    <>
      <Seo title="Admin" noindex />
      <Show when="signed-in"><Shell /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
